import React from 'react';
import { DateFieldProps } from './types';

// Utility functions for date formatting
const formatDateForInput = (isoString: string): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';

    if (isoString.includes('T')) {
      // Has time component - return datetime-local format
      const localDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );
      return localDate.toISOString().slice(0, 16);
    } else {
      // Date only - return YYYY-MM-DD format
      return date.toISOString().split('T')[0];
    }
  } catch {
    return '';
  }
};

const parseDateFromInput = (
  inputValue: string,
  includeTime: boolean = false
): string => {
  if (!inputValue) return '';
  try {
    const date = new Date(inputValue);
    if (isNaN(date.getTime())) return '';

    return includeTime ? date.toISOString() : date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const DateField: React.FC<DateFieldProps> = ({
  id,
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  onFocus,
  onBlur,
  hasValue,
  getFloatingLabelStyle,
  getFloatingInputStyle,
  styles,
  includeTime = false,
}) => {
  const inputType = includeTime ? 'datetime-local' : 'date';
  const formattedValue = formatDateForInput(value || '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return;
    const newValue = parseDateFromInput(e.target.value, includeTime);
    onChange(newValue);
  };

  return (
    <div style={styles.floatingFieldContainer}>
      <input
        id={id}
        type={inputType}
        value={formattedValue}
        onChange={handleChange}
        disabled={disabled}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{
          ...getFloatingInputStyle(false, id),
          colorScheme: 'light', // Ensures calendar icon is visible
          paddingRight: '8px', // Space for calendar icon
        }}
      />
      <label htmlFor={id} style={getFloatingLabelStyle(id, hasValue)}>
        {label}
        {required && <span style={{ color: 'red' }}>*</span>}
      </label>
    </div>
  );
};

export default React.memo(DateField);
